import React from 'react';
import { useI18n } from '../context/I18nContext';
import { Link } from 'react-router-dom';
import { LogoIcon } from '../components/Icons';
import { useSettings } from '../context/SettingsContext';

const PrivacyPolicy = () => {
  const { settings, getCityState } = useSettings();

  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <LogoIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">{settings.companyName}</span>
          </Link>
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            Voltar ao inicio
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('pp.title')}</h1>
        {/* So aparece em ingles: o corpo das clausulas continua em portugues. */}
        {t('legal.notice') && (
          <p className="mb-8 -mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {t('legal.notice')}
          </p>
        )}

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-6">
          <p className="text-gray-600">
            Ultima atualizacao: {new Date().toLocaleDateString('pt-BR')}
          </p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.1')}</h2>
            <p className="text-gray-600 leading-relaxed">
              A {settings.companyName} ("nos", "nosso" ou "empresa") esta comprometida em proteger a privacidade dos usuarios
              de nossos servicos. Esta Politica de Privacidade explica como coletamos, usamos, divulgamos e
              protegemos suas informacoes pessoais quando voce utiliza nossa plataforma de gestao de conversas.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.2')}</h2>
            <p className="text-gray-600 leading-relaxed">Coletamos os seguintes tipos de informacoes:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Informacoes de cadastro:</strong> nome, e-mail, telefone e dados da empresa.</li>
              <li><strong>Dados de uso:</strong> informacoes sobre como voce utiliza nossa plataforma.</li>
              <li><strong>Dados de conversas:</strong> mensagens enviadas e recebidas atraves da plataforma.</li>
              <li><strong>Informacoes tecnicas:</strong> endereco IP, tipo de navegador, dispositivo utilizado.</li>
              <li><strong>Cookies e tecnologias similares:</strong> para melhorar sua experiencia de navegacao.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.3')}</h2>
            <p className="text-gray-600 leading-relaxed">Utilizamos suas informacoes para:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Fornecer e manter nossos servicos.</li>
              <li>Processar transacoes e enviar notificacoes relacionadas.</li>
              <li>Melhorar e personalizar sua experiencia na plataforma.</li>
              <li>Enviar comunicacoes de marketing (com seu consentimento).</li>
              <li>Cumprir obrigacoes legais e regulatorias.</li>
              <li>Detectar e prevenir fraudes e abusos.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.4')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Nao vendemos suas informacoes pessoais. Podemos compartilhar dados com:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provedores de servicos que nos auxiliam na operacao da plataforma.</li>
              <li>Parceiros de negocios, mediante seu consentimento.</li>
              <li>Autoridades governamentais, quando exigido por lei.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.5')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Utilizamos cookies e tecnologias similares para coletar informacoes sobre sua navegacao,
              personalizar conteudo e anuncios, fornecer funcionalidades de redes sociais e analisar
              nosso trafego. Voce pode gerenciar suas preferencias de cookies atraves das configuracoes
              do seu navegador.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.6')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Implementamos medidas de seguranca tecnicas e organizacionais para proteger suas informacoes
              contra acesso nao autorizado, alteracao, divulgacao ou destruicao. Isso inclui criptografia
              de dados, firewalls e controles de acesso.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.7')}</h2>
            <p className="text-gray-600 leading-relaxed">
              De acordo com a Lei Geral de Protecao de Dados (LGPD), voce tem direito a:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Confirmar a existencia de tratamento de dados.</li>
              <li>Acessar seus dados pessoais.</li>
              <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
              <li>Solicitar a anonimizacao, bloqueio ou eliminacao de dados.</li>
              <li>Solicitar a portabilidade dos dados.</li>
              <li>Revogar o consentimento a qualquer momento.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.8')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Mantemos suas informacoes pessoais pelo tempo necessario para cumprir as finalidades
              descritas nesta politica, a menos que um periodo de retencao mais longo seja exigido
              ou permitido por lei.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.9')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Para exercer seus direitos ou esclarecer duvidas sobre esta politica, entre em contato:
            </p>
            <div className="bg-gray-50 rounded-xl p-4 text-gray-600">
              <p><strong>{settings.companyName}</strong></p>
              {settings.cnpj && <p>CNPJ: {settings.cnpj}</p>}
              {settings.dpoEmail && <p>E-mail DPO: {settings.dpoEmail}</p>}
              {settings.email && <p>E-mail: {settings.email}</p>}
              {settings.phone && <p>Telefone: {settings.phone}</p>}
              {settings.address && (
                <p>Endereco: {settings.address}{getCityState() ? ` - ${getCityState()}` : ''}{settings.zipCode ? ` - CEP ${settings.zipCode}` : ''}</p>
              )}
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('pp.10')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Podemos atualizar esta Politica de Privacidade periodicamente. Notificaremos sobre
              quaisquer alteracoes publicando a nova politica nesta pagina e atualizando a data
              de "ultima atualizacao".
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} {settings.companyName}. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
